import { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import HadithResultItem from '@components/results/items/HadithResultItem';
import MySnackbar, {
    type MySnackbarProps,
} from '@components/snackbar/Snackbar';

import { useBookmarkContext } from '@contexts/useBookmarkContext';
import { type IFolder, useBookmarks } from '@hooks/useBookmarks';
import useTranslation from '@hooks/useTranslation';
import { useUI } from '@hooks/useUI';
import { Box, Skeleton, Typography } from '@mui/material';

import type { RoadsHadith } from 'shared/interfaces/hadith';

export function BookmarkedHadithsResult() {
    const router = useRouter();
    const { isMobileOrTablet } = useUI();
    const { hadiths, setHadiths, setFolders, folders } = useBookmarkContext();
    const [hadithsLoading, setHadithsLoading] = useState(false);
    const { getStorageFolders, getFolder, fetchHadiths } = useBookmarks();
    const [showSnackbar, setShowSnackbar] = useState<MySnackbarProps>({
        message: '',
        show: false,
    });
    const fetchData = async () => {
        if (!router.query.folder) {
            setHadiths([]);
            return;
        }

        const folders = await getStorageFolders();
        setFolders(folders);

        if (!folders.length) {
            setHadiths([]);
            return;
        }

        const folder = await getFolder(router.query.folder as string);
        setHadithsLoading(true);
        const hadithsData = await fetchHadiths(folder.hadith_ids);
        setHadiths(hadithsData);
        setHadithsLoading(false);
    };

    useEffect(() => {
        if (router.query.folder) {
            fetchData();
        }
    }, [router.query.folder]);

    return (
        <Box>
            {hadithsLoading && (
                <Skeleton
                    variant="rectangular"
                    height={100}
                    sx={{ borderRadius: 2 }}
                />
            )}

            <HadithList
                hadiths={hadiths}
                hadithsLoading={hadithsLoading}
                setShowSnackbar={setShowSnackbar}
            />

            <NoBookmarksMessage
                folders={folders}
                hadiths={hadiths}
                hadithsLoading={hadithsLoading}
            />

            <MySnackbar
                anchorOrigin={{
                    vertical: isMobileOrTablet ? 'top' : 'bottom',
                    horizontal: isMobileOrTablet ? 'center' : 'right',
                }}
                message={showSnackbar.message}
                key="top-center"
                open={showSnackbar.show}
                onClose={() => setShowSnackbar({ message: '', show: false })}
                autoHideDuration={3000}
                severity={showSnackbar.severity}
            />
        </Box>
    );
}

function HadithList({
    hadiths,
    hadithsLoading,
    setShowSnackbar,
}: {
    hadiths: RoadsHadith[];
    hadithsLoading: boolean;
    setShowSnackbar: (props: MySnackbarProps) => void;
}) {
    const { isMobileOrTablet } = useUI();
    if (!hadiths.length || hadithsLoading) {
        return null;
    }

    return (
        <>
            {hadiths.map((hadith) => (
                <HadithResultItem
                    key={hadith.hadith_id}
                    rightActions={[]}
                    hadith_object={hadith}
                    allowNarratorClick={false}
                    setShowSnackbar={setShowSnackbar}
                    sx={
                        hadiths.length > 1
                            ? isMobileOrTablet
                                ? { my: 2 }
                                : { mb: 2 }
                            : {}
                    }
                />
            ))}
        </>
    );
}

function NoBookmarksMessage({
    folders,
    hadiths,
    hadithsLoading,
}: {
    folders: IFolder[];
    hadiths: RoadsHadith[];
    hadithsLoading: boolean;
}) {
    const { t } = useTranslation('bookmark');

    if (hadithsLoading || hadiths.length) {
        return null;
    }

    return (
        <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            py={10}
        >
            {folders.length
                ? t('no_bookmarks_found')
                : t('add_folder_and_add_hadiths')}
        </Typography>
    );
}
